// web/src/lib/theme.ts — appearance state (mode + accent), localStorage-backed.
// Initial paint is set by the inline boot script in index.html (no flash); this owns
// runtime changes (Settings) and the live system-preference watcher.

export type Mode = 'light' | 'dark' | 'system';
export type Accent = 'brand' | 'blue' | 'gold' | 'fire' | 'graphite' | 'emerald';

// Swatch = the bright hue shown in the picker (not the AA button shade).
export const ACCENTS: { id: Accent; label: string; swatch: string }[] = [
  { id: 'brand', label: 'Brand', swatch: '#FF5C1A' },
  { id: 'blue', label: 'Blue', swatch: '#3B82F6' },
  { id: 'gold', label: 'Gold', swatch: '#FBBF24' },
  { id: 'fire', label: 'Fire', swatch: '#F97316' },
  { id: 'graphite', label: 'Graphite', swatch: '#94A3B8' },
  { id: 'emerald', label: 'Emerald', swatch: '#10B981' },
];

const MODE_KEY = 'yp.mode';
const ACCENT_KEY = 'yp.accent';
const ACCENT_IDS: Accent[] = ['brand', 'blue', 'gold', 'fire', 'graphite', 'emerald'];

const prefersDark = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

export function readMode(): Mode {
  const v = typeof window !== 'undefined' ? window.localStorage.getItem(MODE_KEY) : null;
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}
export function readAccent(): Accent {
  const v = typeof window !== 'undefined' ? window.localStorage.getItem(ACCENT_KEY) : null;
  return ACCENT_IDS.includes(v as Accent) ? (v as Accent) : 'brand';
}

export function resolveMode(mode: Mode): 'light' | 'dark' {
  return mode === 'dark' || (mode === 'system' && prefersDark()) ? 'dark' : 'light';
}

export function applyMode(mode: Mode): void {
  if (typeof document !== 'undefined') document.documentElement.setAttribute('data-mode', resolveMode(mode));
}
export function applyAccent(accent: Accent): void {
  if (typeof document !== 'undefined') document.documentElement.setAttribute('data-accent', accent);
}

export function setMode(mode: Mode): void {
  if (typeof window !== 'undefined') window.localStorage.setItem(MODE_KEY, mode);
  applyMode(mode);
}
export function setAccent(accent: Accent): void {
  if (typeof window !== 'undefined') window.localStorage.setItem(ACCENT_KEY, accent);
  applyAccent(accent);
}

// Call once at boot: keep 'system' mode in sync with OS changes app-wide.
export function initThemeWatcher(): void {
  if (typeof window === 'undefined') return;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  mq.addEventListener('change', () => {
    if (readMode() === 'system') applyMode('system');
  });
}
